import {
    Card,
    CardContent,
    Typography,
    Box,
    Avatar,
    IconButton,
} from '@mui/material'
import { MessageSquare, X } from 'lucide-react'

interface CommentData {
    comment: string
    commentBy: string
    commentDate: string
}

interface CommentCardProps {
    data: CommentData
    variant?: 'default' | 'compact'
    onDelete?: () => void
}

export default function TicketCommentCard({
    data,
    variant = 'default',
    onDelete,
}: CommentCardProps) {
    const { comment, commentBy, commentDate } = data

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    return (
        <Card
            sx={{
                mb: 2,
                boxShadow: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                },
            }}
        >
            <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar
                        sx={{
                            bgcolor: 'primary.main',
                            width: variant === 'compact' ? 32 : 40,
                            height: variant === 'compact' ? 32 : 40,
                        }}
                    >
                        <MessageSquare size={variant === 'compact' ? 16 : 20} />
                    </Avatar>

                    <Box flex={1}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                        >
                            <Typography
                                variant={
                                    variant === 'compact'
                                        ? 'subtitle2'
                                        : 'subtitle1'
                                }
                                fontWeight="600"
                                color="text.primary"
                            >
                                {commentBy}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {formatDate(commentDate)}
                            </Typography>
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {comment}
                        </Typography>
                    </Box>

                    {onDelete && (
                        <IconButton
                            onClick={onDelete}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    color: 'error.main',
                                    bgcolor: 'error.lighter',
                                },
                            }}
                            aria-label="delete comment"
                        >
                            <X size={18} />
                        </IconButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}
